/**
 * BusTopology component for Infrastructure Map
 *
 * Renders the bus network infrastructure:
 * - Horizontal bus backbone line
 * - Connection points (dots) along the bus
 * - Drop lines from nodes to their connection points
 * - Collapse indicators for hub nodes with children
 */

import { useState, type ReactElement } from 'react'
import {
  BUS_PADDING_X,
  BusBackbone,
  BusNodeMarker,
  HubDropLine,
  type BusNode,
} from './bus-topology/bus-topology.parts'

interface BusTopologyProps {
  nodes: BusNode[]
  busY: number
  lanGroupId: number
  onToggleCollapse?: (endpointId: string) => void
  onToggleCollapseRecursive?: (endpointId: string) => void
}

export function BusTopology({
  nodes,
  busY,
  lanGroupId,
  onToggleCollapse,
  onToggleCollapseRecursive,
}: BusTopologyProps): ReactElement | null {
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null)

  if (nodes.length === 0) return null

  const xPositions = nodes.map((node) => node.position.x)
  const minX = Math.min(...xPositions) - BUS_PADDING_X
  const maxX = Math.max(...xPositions) + BUS_PADDING_X
  const hubNode = nodes.find((node) => node.isHub)

  return (
    <g data-bus-topology={lanGroupId}>
      <BusBackbone minX={minX} maxX={maxX} busY={busY} />
      {nodes.map((node) => (
        <BusNodeMarker
          key={node.endpointId}
          node={node}
          busY={busY}
          hoveredNodeId={hoveredNodeId}
          setHoveredNodeId={setHoveredNodeId}
          onToggleCollapse={onToggleCollapse}
          onToggleCollapseRecursive={onToggleCollapseRecursive}
        />
      ))}
      <HubDropLine hubNode={hubNode} busY={busY} />
    </g>
  )
}

export default BusTopology
